<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useDocSearch } from '@/composables/useDocSearch'
import { useLocale } from '@/composables/useLocale'
import type { DocSearchResult } from '@/types/docs'

const props = defineProps<{
  open: boolean
  base: string
}>()

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const router = useRouter()
const { currentLocale } = useLocale()
const { ensureLoaded, search, loading } = useDocSearch()

const query = ref('')
const activeIndex = ref(0)
const inputRef = ref<HTMLInputElement | null>(null)

const results = computed<DocSearchResult[]>(() => {
  if (query.value.trim().length < 2) return []
  return search(query.value, currentLocale.value)
})

const resultCount = computed(() => results.value.length)

// Rewrite /docs/ prefix to /organizer/docs/ when inside the dashboard
function resolveUrl(entry: DocSearchResult): string {
  return `${props.base}/${entry.category}/${entry.slug}`
}

function close(): void {
  emit('close')
  query.value = ''
  activeIndex.value = 0
}

function navigate(entry: DocSearchResult): void {
  void router.push(resolveUrl(entry))
  close()
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    close()
    return
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    if (results.value.length > 0) {
      activeIndex.value = (activeIndex.value + 1) % results.value.length
    }
    return
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    if (results.value.length > 0) {
      activeIndex.value =
        (activeIndex.value - 1 + results.value.length) % results.value.length
    }
    return
  }

  if (event.key === 'Enter') {
    event.preventDefault()
    const selected = results.value[activeIndex.value]
    if (selected) navigate(selected)
    return
  }
}

// Reset active index when results change
watch(results, () => {
  activeIndex.value = 0
})

// Load index and focus input when palette opens
watch(
  () => props.open,
  async (isOpen) => {
    if (isOpen) {
      void ensureLoaded(currentLocale.value)
      await nextTick()
      inputRef.value?.focus()
    } else {
      query.value = ''
    }
  },
)

// Global Cmd/Ctrl+K handler — only active while palette is open or used as toggle
function handleGlobalKeydown(event: KeyboardEvent): void {
  if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
    event.preventDefault()
    if (props.open) {
      close()
    }
    // If not open, the parent page listens for Cmd+K and opens it
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleGlobalKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalKeydown)
})

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function highlightMatch(text: string, q: string): string {
  const safe = escapeHtml(text)
  if (!q.trim()) return safe
  const escaped = q.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return safe.replace(
    new RegExp(`(${escaped})`, 'gi'),
    '<mark class="bg-emerald-500/20 text-emerald-300 rounded-sm px-0.5">$1</mark>',
  )
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4 sm:px-6"
      role="dialog"
      aria-modal="true"
      :aria-label="t('docs.search.aria_label')"
      @keydown="onKeydown"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black/60 backdrop-blur-sm"
        @click="close"
      />

      <!-- Panel -->
      <div class="relative w-full max-w-2xl rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl overflow-hidden">
        <!-- Search input -->
        <div class="flex items-center gap-3 px-4 py-3 border-b border-slate-800">
          <svg
            class="w-4 h-4 text-slate-400 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            ref="inputRef"
            v-model="query"
            type="text"
            class="flex-1 bg-transparent text-white placeholder-slate-500 text-sm focus:outline-none"
            :placeholder="t('docs.search.placeholder')"
            role="combobox"
            aria-haspopup="listbox"
            :aria-expanded="results.length > 0"
            aria-controls="docs-search-results"
            :aria-activedescendant="results[activeIndex] ? `docs-result-${activeIndex}` : undefined"
            autocomplete="off"
            spellcheck="false"
          >
          <button
            type="button"
            class="text-xs text-slate-500 hover:text-slate-300 shrink-0"
            @click="close"
          >
            {{ t('docs.search.kbd_close') }}
            <kbd class="ml-1 px-1.5 py-0.5 rounded bg-slate-800 border border-slate-600 text-slate-400 font-mono text-xs">Esc</kbd>
          </button>
        </div>

        <!-- Loading -->
        <div
          v-if="loading"
          class="px-4 py-6 text-center text-sm text-slate-500"
        >
          {{ t('docs.search.loading') }}
        </div>

        <!-- Hint (empty query) -->
        <div
          v-else-if="query.trim().length < 2"
          class="px-4 py-6 text-center text-sm text-slate-500"
        >
          {{ t('docs.search.hint') }}
        </div>

        <!-- No results -->
        <div
          v-else-if="results.length === 0"
          class="px-4 py-6 text-center text-sm text-slate-500"
        >
          {{ t('docs.search.no_results') }}
          <RouterLink
            :to="base"
            class="ml-1 text-emerald-400 hover:underline"
            @click="close"
          >
            {{ t('docs.hub.browse_heading') }}
          </RouterLink>
        </div>

        <!-- Results -->
        <ul
          v-else
          id="docs-search-results"
          role="listbox"
          class="max-h-96 overflow-y-auto divide-y divide-slate-800"
        >
          <li
            v-for="(result, index) in results"
            :id="`docs-result-${index}`"
            :key="result.id"
            role="option"
            :aria-selected="index === activeIndex"
            class="flex flex-col px-4 py-3 cursor-pointer transition-colors"
            :class="index === activeIndex ? 'bg-slate-800' : 'hover:bg-slate-800/50'"
            @click="navigate(result)"
            @mouseenter="activeIndex = index"
          >
            <!-- Category label -->
            <span class="text-xs text-slate-500 mb-0.5">
              {{ t(`docs.category.${result.category}.title`) }}
            </span>
            <!-- Title -->
            <span
              class="text-sm font-medium text-white"
              v-html="highlightMatch(result.title, query)"
            />
            <!-- Excerpt -->
            <span
              v-if="result.excerpt"
              class="mt-0.5 text-xs text-slate-400 line-clamp-2"
              v-html="highlightMatch(result.excerpt, query)"
            />
          </li>
        </ul>

        <!-- Footer with keyboard hints -->
        <div class="flex items-center gap-4 px-4 py-2 border-t border-slate-800 text-xs text-slate-600">
          <span>
            <kbd class="px-1 py-0.5 rounded bg-slate-800 border border-slate-700 font-mono text-slate-500">↑↓</kbd>
            {{ t('docs.search.kbd_navigate') }}
          </span>
          <span>
            <kbd class="px-1 py-0.5 rounded bg-slate-800 border border-slate-700 font-mono text-slate-500">↵</kbd>
            {{ t('docs.search.kbd_open') }}
          </span>
          <!-- Live region for screen readers -->
          <span
            aria-live="polite"
            aria-atomic="true"
            class="sr-only"
          >
            {{ resultCount > 0 ? t('docs.search.result_count', { count: resultCount }, resultCount) : '' }}
          </span>
        </div>
      </div>
    </div>
  </Teleport>
</template>
